import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

export default function ExportSettings({ visible, onHide, columns = [], initialPreset = '', onSaved = () => {}, onRemoved = () => {} }) {
  const toast = useRef(null);
  const [list, setList] = useState([]);
  const [presetName, setPresetName] = useState(initialPreset);
  const STORAGE_KEY = 'enrichment_presets';

  // initialize when sidebar opens
  useEffect(() => {
    if (!visible) return;
    setPresetName(initialPreset || '');
    // if editing an existing preset (not 'Default') try to load saved columns from localStorage
    if (initialPreset && initialPreset !== 'Default') {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const obj = raw ? JSON.parse(raw) : null;
        if (obj && Array.isArray(obj[initialPreset])) {
          setList([...obj[initialPreset]]);
          return;
        }
      } catch (err) {
        // fall back to provided columns prop
      }
    }
    // default: use columns prop (mergedColumns)
    setList(Array.isArray(columns) ? [...columns] : []);
  }, [visible, columns, initialPreset]);

  const moveUp = (idx) => {
    if (idx <= 0) return;
    const copy = [...list];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    setList(copy);
  };

  const moveDown = (idx) => {
    if (idx >= list.length - 1) return;
    const copy = [...list];
    [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
    setList(copy);
  };

  const moveToTop = (idx) => {
    if (idx <= 0) return;
    const copy = [...list];
    const [item] = copy.splice(idx, 1);
    copy.unshift(item);
    setList(copy);
  };

  const moveToBottom = (idx) => {
    if (idx >= list.length - 1) return;
    const copy = [...list];
    const [item] = copy.splice(idx, 1);
    copy.push(item);
    setList(copy);
  };

  const handleSave = () => {
    const name = (presetName || '').trim();
    if (!name) {
      if (toast.current) toast.current.show({ severity: 'warn', summary: 'Preset name required', detail: 'Enter a preset name before saving', life: 3000 });
      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      obj[name] = list;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      if (toast.current) toast.current.show({ severity: 'success', summary: 'Saved', detail: `Preset "${name}" saved`, life: 3000 });
      onSaved(name, list);
    } catch (err) {
      if (toast.current) toast.current.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save preset', life: 4000 });
    }
  };

  const handleRemove = () => {
    const name = (presetName || '').trim();
    if (!name || name.toLowerCase() === 'default') {
      if (toast.current) toast.current.show({ severity: 'warn', summary: 'Cannot remove', detail: 'Default cannot be removed', life: 3000 });
      return;
    }

    if (!window.confirm(`Remove preset "${name}"? This cannot be undone.`)) return;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      if (obj && obj[name]) {
        delete obj[name];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      }
      if (toast.current) toast.current.show({ severity: 'success', summary: 'Removed', detail: `Preset "${name}" removed`, life: 3000 });
      // notify parent
      onRemoved(name);
      // reset local state
      setPresetName('');
      setList(Array.isArray(columns) ? [...columns] : []);
    } catch (err) {
      if (toast.current) toast.current.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to remove preset', life: 4000 });
    }
  };

  return (
    // make component fill sidebar height and arrange sections so column list occupies remaining space
    <div style={{ padding: '0 0.5rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toast ref={toast} />

      {/* Preset name row: input + remove button (remove only enabled when name equals initialPreset and not 'default') */}
      <div style={{ marginBottom: '.75rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: '0 0 auto', minWidth: 100 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>Preset name</label>
        </div>
        <div style={{ flex: '1 1 auto' }}>
          <InputText value={presetName} onChange={(e) => setPresetName(e.target.value)} style={{ width: '100%' }} placeholder="Enter preset name" />
        </div>
        <div style={{ flex: '0 0 auto', marginTop: 6 }}>
          { /* only allow remove when preset name matches initialPreset and is not 'Default' */ }
          <Button
            label="Remove"
            icon="pi pi-trash"
            className="p-button-danger"
            onClick={handleRemove}
            disabled={
              !(presetName && presetName === initialPreset && presetName !== 'Default')
            }
          />
        </div>
      </div>

      <div style={{ marginBottom: '.5rem', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>Columns (use up / down to reorder)</label>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 6 }}>
          {list.length === 0 && <div style={{ color: '#666', padding: 8 }}>No columns</div>}
          {list.map((col, idx) => (
            <div key={col + '_' + idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 4px', borderBottom: '1px solid #f3f3f3' }}>
              <div style={{ fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{col}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {/* move to top */}
                <Button
                  icon="pi pi-angle-double-up"
                  className="p-button-sm p-button-text"
                  onClick={() => moveToTop(idx)}
                  disabled={idx === 0}
                  aria-label={`Move ${col} to top`}
                  style={{ padding: '0.25rem' }}
                />
                {/* move up one */}
                <Button
                  icon="pi pi-chevron-up"
                  className="p-button-sm p-button-text"
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  aria-label={`Move ${col} up`}
                  style={{ padding: '0.25rem' }}
                />
                {/* move down one */}
                <Button
                  icon="pi pi-chevron-down"
                  className="p-button-sm p-button-text"
                  onClick={() => moveDown(idx)}
                  disabled={idx === list.length - 1}
                  aria-label={`Move ${col} down`}
                  style={{ padding: '0.25rem' }}
                />
                {/* move to bottom */}
                <Button
                  icon="pi pi-angle-double-down"
                  className="p-button-sm p-button-text"
                  onClick={() => moveToBottom(idx)}
                  disabled={idx === list.length - 1}
                  aria-label={`Move ${col} to bottom`}
                  style={{ padding: '0.25rem' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button label="Save preset" icon="pi pi-save" onClick={handleSave} className="p-button-primary" disabled={presetName === 'Default'} />
        <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-secondary" />
      </div>
    </div>
  );
}